import { FC, useCallback, useRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Button, DropDownModal, TabsEditable } from '@marriott/mi-ui-library';
import { CalendarTabValue, DesktopCalendarProps } from './DesktopCalendar.types';
import { Calendar } from '../Calendar';
import { StyledDesktopCalendar } from './DesktopCalendar.styles';
import { CALENDAR_VARIATION_DESKTOP, CALENDAR_START_DATE, ENTER_KEY } from '../../../constants';
import { useFocusOut } from '../../../hooks/useFocusOut';
import { setCalendarOptions, getResetSelectionForTab } from '../../../utils';
import { TextFormField } from '../../TextFormField';
import { useCalendarTabState } from '../useCalendarTabState';

const CALENDAR_MODE_BY_TAB: Record<CalendarTabValue, 'single' | 'range'> = {
  [CalendarTabValue.MULTIPLE]: 'range',
  [CalendarTabValue.SINGLE]: 'single',
};

export const DesktopCalendar: FC<DesktopCalendarProps> = ({
  dates,
  ctas,
  align = 'right',
  selectedStartDate,
  selectedEndDate,
  isDateRangeFixed = false,
  rangeStartDate,
  rangeEndDate,
  setDefaultDates = false,
  showErrorMessage = false,
  errorMessage,
  resetBtnClickTrackLocation,
  isPastDateSelectionDisabled,
  enableFromPastDate,
  clearDates,
  onChange,
  onClose,
  calendarTab,
  onCalendarTabChange,
  isEventSpaceTabActive = false,
  hideDatesBadge = false,
}) => {
  const {
    label,
    placeholder,
    datePlaceholder,
    night,
    nights,
    days,
    shortWeekdayInitials,
    shortWeekdayNames,
    longWeekdayNames,
    shortMonthNames,
    longMonthNames,
    multipleDays,
    singleDay,
  } = dates;
  const { reset } = ctas;

  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const [calendarResetKey, setCalendarResetKey] = useState(0);

  const calendarRef = useRef<HTMLElement>(null);
  const datesInputRef = useRef<HTMLInputElement>(null);
  const focusOutRefs = useRef<Array<HTMLElement | null>>([null, null]);

  const {
    currentActiveTab,
    calendarState,
    startDate,
    endDate,
    displayState,
    tabList,
    setCalendarStateByTab,
    setInitialDates,
    updateCurrentActiveTab,
    setDatesForCurrentTab,
  } = useCalendarTabState({
    calendarTab,
    selectedStartDate,
    selectedEndDate,
    night,
    nights,
    days,
    multipleDays,
    singleDay,
    isEventSpaceTabActive,
    onChange,
    onCalendarTabChange,
  });

  focusOutRefs.current[0] = datesInputRef.current;
  focusOutRefs.current[1] = calendarRef.current;

  useEffect(() => {
    if (!isFlyoutOpen) {
      return;
    }

    setCalendarOptions({
      shortWeekdayNames,
      longWeekdayNames,
      shortMonthNames,
      longMonthNames,
    });
  }, [isFlyoutOpen, shortWeekdayNames, longWeekdayNames, shortMonthNames, longMonthNames]);

  const closeFlyout = useCallback(() => {
    setIsFlyoutOpen(false);
    onClose?.();
  }, [onClose]);

  const toggleFlyout = () => {
    setIsFlyoutOpen(prevState => !prevState);
  };

  useFocusOut({
    ref: focusOutRefs.current,
    onFocusOut: (isFocusOut: boolean) => {
      if (isFocusOut) {
        closeFlyout();
      }
    },
    handleKeyup: true,
  });

  const handleContainerBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (isFlyoutOpen && !event.currentTarget.contains(event.relatedTarget as Node)) {
        closeFlyout();
      }
    },
    [isFlyoutOpen, closeFlyout]
  );

  const resetAllTabDates = useCallback(() => {
    setCalendarStateByTab(prevState => {
      const updatedState = { ...prevState };

      Object.values(CalendarTabValue).forEach(tab => {
        const resetSelection = getResetSelectionForTab(
          tab,
          setDefaultDates,
          isDateRangeFixed,
          rangeStartDate,
          rangeEndDate
        );

        updatedState[tab] = {
          ...prevState[tab],
          startDate: resetSelection.startDate,
          endDate: resetSelection.endDate,
          selectedDateType: resetSelection.selectedDateType,
        };
      });

      return updatedState;
    });
    setCalendarResetKey(prev => prev + 1);
  }, [setCalendarStateByTab, setDefaultDates, isDateRangeFixed, rangeStartDate, rangeEndDate]);

  useEffect(() => {
    if (clearDates) {
      resetAllTabDates();
    }
  }, [clearDates, resetAllTabDates]);

  const resetCta = (
    <Button
      callback={resetAllTabDates}
      className={clsx('reset-cta', 'm-link-action', 'p-0')}
      custom_click_track_value={`${resetBtnClickTrackLocation}| ${reset} button |internal`}
      buttonCopy={reset}
      testId="reset"
    />
  );

  const calendar = (
    <Calendar
      key={calendarResetKey}
      mode={CALENDAR_MODE_BY_TAB[currentActiveTab]}
      variation={CALENDAR_VARIATION_DESKTOP}
      nightLabel={night}
      nightsLabel={nights}
      shortWeekdayInitials={shortWeekdayInitials}
      shortWeekdayNames={shortWeekdayNames}
      longWeekdayNames={longWeekdayNames}
      shortMonthNames={shortMonthNames}
      longMonthNames={longMonthNames}
      setInitialDates={(startDate, endDate, initialStartDate, initialEndDate, selectedDateType) =>
        setInitialDates(currentActiveTab, startDate, endDate, initialStartDate, initialEndDate, selectedDateType)
      }
      setDates={(startDate, endDate, selectedDateType) =>
        setDatesForCurrentTab(startDate, endDate || null, selectedDateType)
      }
      selectedStartDate={startDate}
      selectedEndDate={endDate}
      isDateRangeFixed={currentActiveTab === CalendarTabValue.MULTIPLE ? isDateRangeFixed : false}
      rangeStartDate={currentActiveTab === CalendarTabValue.MULTIPLE ? rangeStartDate : null}
      rangeEndDate={currentActiveTab === CalendarTabValue.MULTIPLE ? rangeEndDate : null}
      setDefaultDates={setDefaultDates}
      isCalendarClosed={!isFlyoutOpen}
      isPastDateSelectionDisabled={isPastDateSelectionDisabled}
      enableFromPastDate={enableFromPastDate}
      selectedDateType={calendarState.selectedDateType || CALENDAR_START_DATE}
    />
  );

  return (
    <StyledDesktopCalendar
      data-testid="groups-DesktopCalendar"
      data-component-name="m-groups-DesktopCalendar"
      align={align}
      onBlur={handleContainerBlur}
    >
      <section>
        <TextFormField
          ref={datesInputRef}
          label={label}
          ariaLabel={dates['ariaLabel']}
          placeholder={currentActiveTab === CalendarTabValue.SINGLE ? datePlaceholder : placeholder}
          value={displayState.dateInputValue}
          className="m-input-text-field"
          hideCursor={true}
          showErrorMessage={showErrorMessage}
          errorMessage={errorMessage}
          testId="dates"
          onClick={toggleFlyout}
          showBadgeContent={!hideDatesBadge && displayState.showDatesBadgeContent}
          badgeContent={!hideDatesBadge ? displayState.datesLabel : ''}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === ENTER_KEY) {
              event.preventDefault();
              toggleFlyout();
            }
          }}
        />
      </section>
      <section ref={calendarRef} className="date-picker-modal">
        <DropDownModal
          className="groups-calendar-modal"
          show={isFlyoutOpen}
          handleBlur={true}
          dropdownModalOpenState={isFlyoutOpen}
          dropdownModalOnCLoseFunc={closeFlyout}
          setDropdownModalOpenState={closeFlyout}
        >
          <section className="groups-tab-container">
            <TabsEditable
              tabList={tabList}
              isSwitcherTabs={true}
              isTablink={true}
              classTabsEnabled="Tabs Enabled"
              isIconTabs={false}
              selectedTab={currentActiveTab}
              children={calendar}
              setSelectedTab={updateCurrentActiveTab}
            />
          </section>
          <section className="calendar-footer align-items-center">
            <div className="p-2 pl-0">{resetCta}</div>
            {currentActiveTab === CalendarTabValue.SINGLE && dates.message && (
              <div className="calendar-badge">{dates.message}</div>
            )}
          </section>
        </DropDownModal>
      </section>
    </StyledDesktopCalendar>
  );
};
